<template>
  <div class="contact-extra">

    <div class="custom-fields-wrapper">
      <div v-for="field in customFields.slice(0, 5)"
           :key="field.label"
           class="info-line">
        <span class="info-line-name">
          {{field.label}}:
        </span>
        <CustomField :field="field"/>
      </div>
    </div>

    <div class="show-more"
         :class="{ 'showing': isShowingMore }"
         @click="toggleShowMore">
      {{showMoreText}}
    </div>

    <div class="extra-info"
         v-if="isShowingMore">

      <div class="custom-fields-wrapper">
        <div v-for="field in customFields.slice(5, customFields.length)"
             :key="field.label"
             class="info-line">
          <span class="info-line-name">
            {{field.label}}:
          </span>
          <CustomField :field="field"/>
        </div>
      </div>

      <hr class="separator">

      <div class="section-title">
        {{$t('client.matters.matter_page.contact.communication_details')}}
      </div>

      <v-tooltip top
                 content-class="edit-tooltip"
                 :disabled="isMobile">
        <template v-slot:activator="{ on, attrs }">
          <v-icon class="edit-button"
                  @click="handleEditButtonClick"
                  v-bind="attrs"
                  v-on="on">
            icon-pencil-s
          </v-icon>
        </template>
        <span>{{$t('common.words.edit')}}</span>
      </v-tooltip>

      <div class="info-line"
           v-if="hasOptInField">
        <span class="info-line-name">
          {{$t('client.matters.matter_page.contact.opted_in_promotions')}}:
        </span>
        <span class="info-line-value">
          {{optInValue}}
        </span>
      </div>

      <div class="info-line"
           v-if="contact.unsubscribed">
        <span class="info-line-name">
          {{$t('client.matters.matter_page.contact.unsubscribed')}}:
        </span>
        <span class="info-line-value">
          {{$t('client.client_card.value.yes_true')}}
        </span>
      </div>

      <div class="info-line">
        <span class="info-line-name">
          {{$t('client.matters.matter_page.contact.sms_phone')}}:
        </span>
        <span class="info-line-value">
          {{contact.notifications_phone && contact.notifications_phone.display_value}}
        </span>
      </div>

      <div class="info-line phone-error"
           v-if="invalidMobileNumber">
        {{$t('client.matters.matter_page.contact.invalid_phone_message')}}
      </div>

      <div class="info-line"
           v-if="showDoubleOptIn">
        <span class="info-line-name">
          {{$t('client.matters.matter_page.contact.opted_in_sms')}}:
        </span>
        <span class="info-line-value">
          {{$t('client.client_card.opt_in.' + contact.double_opt_in_status, {date: contact.double_opt_in_sent_at_h})}}
        </span>
      </div>

      <v-btn class="opt-in-button"
             @click="sendOptIn"
             v-if="canSendOptIn">
        {{$t('client.client_card.send')}}
      </v-btn>

      <div class="info-line">
        <span class="info-line-name">
          {{$t('client.matters.matter_page.contact.timezone')}}:
        </span>
        <span class="info-line-value">
          {{contact.time_zone_with_offset}}
        </span>
      </div>

      <hr class="separator">

      <div class="section-title">
        {{$t('client.matters.matter_page.contact.additional_info')}}
      </div>

      <div class="info-line">
        <span class="info-line-name">
          {{$t('client.client_card.label.created_at')}}:
        </span>
        <span class="info-line-value">
          {{contact.created_at | time_today_or_date}}
        </span>
      </div>

      <div class="info-line">
        <span class="info-line-name">
          {{$t('client.client_card.label.last_client_activity')}}:
        </span>
        <span class="info-line-value">
          {{lastActive}}
        </span>
      </div>

      <div class="info-line">
        <span class="info-line-name">
          {{$t('conversation.info.source')}}:
        </span>
        <span class="info-line-value source-link"
              v-if="sourceDetails"
              @click="handleSourceClick">
          {{sourceName}}
        </span>
        <span class="info-line-value"
              v-else>
          {{sourceName}}
        </span>
      </div>

    </div>

  </div>
</template>

<script>
  import {sourceService} from "@/modules/common/services";
  import {mapGetters} from "vuex";
  import CustomField from "@/modules/common/components/CustomField/CustomField";

  export default {
    name: "ContactExtra",
    components: {CustomField},
    props: {
      contact: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        isShowingMore: false
      }
    },
    computed: {
      ...mapGetters("accountModule", ["isStar"]),
      ...mapGetters("clientFields", ["hasOptInField", "mergedContactFields"]),
      ...mapGetters('mattersModule', ['contactFields']),
      ...mapGetters(['isMobile']),
      invalidMobileNumber() {
        return this.contact.notifications_phone && this.contact.notifications_phone.value && !this.contact.is_notifications_phone_mobile;
      },
      sourceName() {
        const {name} = this.contact.source_data;
        return sourceService.sourceList[name] ? this.$t('conversation.info.source_type.' + name) : (name ? name : this.$t('conversation.info.source_type.unknown'));
      },
      sourceDetails() {
        const {website_url, channel, campaign} = this.contact.source_data;
        return website_url || channel || campaign;
      },
      optInValue() {
        const {typed_custom_fields} = this.contact;
        return this.$t(this.$options.filters.yesNoValue(typed_custom_fields ? parseInt(typed_custom_fields.opt_in) : 0));
      },
      isTatango() {
        return this.$hasFeature('tatango');
      },
      showDoubleOptIn() {
        return this.isTatango && this.contact.typed_custom_fields && this.contact.typed_custom_fields.opt_in === '1';
      },
      canSendOptIn() {
        return this.showDoubleOptIn && this.contact.is_notifications_phone_mobile && this.contact.double_opt_in_status === 'not_sent';
      },
      customFields() {
        const nonCustomFields = ['firstname', 'lastname', 'email', 'opt-in', 'address'];
        const fieldLabels = this.mergedContactFields.filter(field => !field.deleted).map(field => field.label);

        let contactFields = this.mergedContactFields.map((field) => {
          let fieldValue, phoneId;

          switch (field.type) {
            case 'identifier':
              fieldValue = this.contact.identifier;
              break;
            case 'phone':
            case 'secondary-phone':
              const phoneData = this.contact.data.find(dataItem => dataItem.field_id === field.id);
              fieldValue = phoneData && phoneData.display_value || null;
              phoneId = phoneData && phoneData.id;
              break;
            default:
              const contactField = this.contactFields.find(f => f.uid === field.uid);
              fieldValue = contactField && contactField.value;
              break;
          }

          return { ...field, value: fieldValue, phoneId: phoneId };
        });

        contactFields = contactFields
          .filter(field => field &&
            !nonCustomFields.includes(field.type) &&
            field.phoneId !== this.contact.notifications_phone_id &&
            (!field.deleted || (field.deleted && field.value)) &&
            !(field.type === 'identifier' && field.deleted))
          .sort((a, b) => a.deleted - b.deleted);

        Object.keys(this.contact.custom_fields).forEach((customField) => {
          if (!fieldLabels.includes(customField) && this.contact.custom_fields[customField]) {
            contactFields.push({
              label: customField,
              value: this.contact.custom_fields[customField],
              type: 'text'
            });
          }
        });

        return contactFields;
      },
      showMoreText() {
        return this.isShowingMore ? this.$t('common.words.show_less') : this.$t('common.words.show_more');
      },
      lastActive() {
        return this.contact.last_client_activity ? this.$options.filters.time_today_or_date(this.contact.last_client_activity) : this.$t('client.client_card.label.not_active');
      }
    },
    methods: {
      handleEditButtonClick() {
        this.$frontageApi.send('matter_action', {action: 'edit_communication_details'});
      },
      handleSourceClick() {
        this.$frontageApi.send('matter_action', {action: 'show_source_dialog', source_data: this.contact.source_data});
        this.$track('Show contact source');
      },
      sendOptIn() {
        this.$frontageApi.send('matter_action', {action: 'send_opt_in'});
      },
      toggleShowMore() {
        this.$track(this.isShowingMore ? 'Show less contact info' : 'Show more contact info');
        this.isShowingMore = !this.isShowingMore;
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  .contact-extra {
    padding-top: 10px;

    .info-line {
      padding-bottom: 8px;

      &-name {
        @include i-padding-mirror(0, 2px);
        color: $grey-color-6;
      }

      &-value {

        #app[dir='rtl'] & {
          unicode-bidi: plaintext;
        }
      }
    }

    .source-link {
      cursor: pointer;
      color: $blue-color-4;
    }

    .phone-error {
      color: $red-color-1;
      font-size: 10px;
      margin-top: -5px;
    }

    .separator {
      border: none;
      height: 1px;
      background-color: $grey-color-2;
      margin: 8px 0;
    }

    .section-title {
      font-weight: 600;
      padding: 8px 0 12px;
    }

    .edit-button {
      @include i-float(right);
      font-size: 16px;
      top: -36px;
      @include i-position-mirror(right, -5px);
      height: 28px;
      width: 28px;
      position: relative;
      color: $grey-color-5;
      border-radius: 50%;
      transition: color 0.2s, background-color 0.2s;

      .matter-page-wrapper &, .matter-pane-desktop & {

        &:hover {
          color: $navy-color-1;
          background-color: $grey-color-10;
        }
      }
    }

    .opt-in-button {
      font-size: 12px;
      text-transform: none;
      letter-spacing: 0;
      height: 28px;
      box-shadow: none;
      margin-bottom: 10px;
    }

    .show-more {
      color: $blue-color-4;
      cursor: pointer;

      &.showing {
        padding-bottom: 8px;
      }
    }
  }

  .edit-tooltip {
    font-family: var(--primary-font-family);
    font-size: 11px;
    line-height: 18px;
    margin-top: 5px;
    padding: 2px 10px;
    z-index: 50 !important;
  }
</style>
